import type { IExtendedUser } from '@shared/types/base'
import type { IInstitution, IPlanInstitucion } from '@/types/base'
import { create } from 'zustand'
import { authLogin, authLogout } from '@/lib/auth-actions'
import type { User } from 'firebase/auth'
import { auth } from '@/firebase/config'

interface AppState {
	user: IExtendedUser | null
	userAuth: User | null
	currentInstitutionId: string | null
	institutions: IInstitution
	planInstitucion: IPlanInstitucion | null
	error: string | null
	loading: boolean
	setUser: (user: IExtendedUser) => void
	setUserAuth: (userAuth: User | null) => void
	setError: (error: string | null) => void
	setLoading: (loading: boolean) => void
	setInstitution: (institution: IInstitution) => void
	setPlanInstitucion: (planInstitucion: IPlanInstitucion) => void
	setCurrentInstitutionId: (currentInstitutionId: string | null) => void
	login: (email: string, password: string) => Promise<void>
	logout: () => Promise<void>
}

/**
 * Estado solo en memoria (misma idea que Vuex en `FrontEnd/certisep/src/store` sin persist).
 * La sesión sigue viniendo de Firebase Auth; no se usa localStorage para el store.
 */
export const useStore = create<AppState>()(set => ({
	user: null,
	userAuth: null,
	institutions: {} as IInstitution,
	currentInstitutionId: null,
	error: null,
	loading: false,
	planInstitucion: null,
	setUser: user => set({ user }),
	setUserAuth: userAuth => set({ userAuth }),
	setError: error => set({ error }),
	setLoading: loading => set({ loading }),
	setInstitution: institution => set({ institutions: institution }),
	setPlanInstitucion: planInstitucion => set({ planInstitucion }),
	setCurrentInstitutionId: currentInstitutionId => set({ currentInstitutionId }),
	login: async (email: string, password: string) => {
		set({ loading: true, error: null })
		try {
			await authLogin(email, password)
			set({ userAuth: auth.currentUser ?? null })
		} catch (err) {
			set({ error: (err as Error).message })
			throw err
		} finally {
			set({ loading: false })
		}
	},
	logout: async () => {
		set({ loading: true, error: null })
		try {
			await authLogout()
			set({
				user: null as IExtendedUser | null,
				userAuth: null,
				institutions: {} as IInstitution,
				currentInstitutionId: null,
				planInstitucion: null as IPlanInstitucion | null
			})
		} catch (err) {
			set({ error: (err as Error).message })
			throw err
		} finally {
			set({ loading: false })
		}
	}
}))
